import React, { useState, useEffect } from "react";
import { CheckCircle, X, AlertCircle, Clock, MessageSquare, Calendar, Eye } from "lucide-react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAPDPsByTeacher,
  acceptAPDP,
  rejectAPDP,
  clearError,
  resetAPDPs,
} from "../../../redux/annualPersonalDevelopmentPlanSlice";

const AdminAPDP = ({ teacherEmail }) => {
  const dispatch = useDispatch();
  const { plans, plansByYear, loading, error, count } = useSelector(
    (state) => state.annualPersonalDevelopmentPlan
  );

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // "accept" or "reject"
  const [adminNotes, setAdminNotes] = useState("");
  const [selectedYear, setSelectedYear] = useState(null);
  const [isYearPlansModalOpen, setIsYearPlansModalOpen] = useState(false);

  // Fetch plans when component mounts or teacherEmail changes
  useEffect(() => {
    if (teacherEmail) {
      dispatch(resetAPDPs());
      dispatch(fetchAPDPsByTeacher(teacherEmail));
    }
  }, [dispatch, teacherEmail]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
    setIsViewModalOpen(true);
  };

  const handleAcceptClick = (plan) => {
    setSelectedPlan(plan);
    setActionType("accept");
    setAdminNotes(plan.adminNotes || "");
    setIsActionModalOpen(true);
  };

  const handleRejectClick = (plan) => {
    setSelectedPlan(plan);
    setActionType("reject");
    setAdminNotes("");
    setIsActionModalOpen(true);
  };

  const handleSubmitAction = async () => {
    if (!selectedPlan) return;

    if (actionType === "reject" && !adminNotes.trim()) {
      toast.error("Admin notes are required when rejecting a plan");
      return;
    }

    try {
      if (actionType === "accept") {
        await dispatch(
          acceptAPDP({
            planId: selectedPlan.id,
            adminNotes: adminNotes.trim() || undefined,
          })
        ).unwrap();
        toast.success("Plan accepted successfully");
      } else {
        await dispatch(
          rejectAPDP({
            planId: selectedPlan.id,
            adminNotes: adminNotes.trim(),
          })
        ).unwrap();
        toast.success("Plan rejected. Employee can now edit it.");
      }
      setIsActionModalOpen(false);
      setSelectedPlan(null);
      setAdminNotes("");
      setActionType(null);
      dispatch(fetchAPDPsByTeacher(teacherEmail));
    } catch (err) {
      toast.error(err || "Failed to process plan");
    }
  };

  const handleViewYearPlans = (year) => {
    setSelectedYear(year);
    setIsYearPlansModalOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "accepted":
        return <CheckCircle size={16} className="text-green-500" />;
      case "rejected":
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  const years = Object.keys(plansByYear || {}).sort((a, b) => b.localeCompare(a));

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h1 className="font-bold text-base text-blue-500">
          APDP ({plans?.length || 0})
        </h1>
      </div>
      {loading && (
        <div className="text-center py-4 text-gray-500">
          <p>Loading plans...</p>
        </div>
      )}
      {!loading && plans && plans.length > 0 ? (
        <div className="space-y-1">
          {years.map((year) => {
            const yearPlans = plansByYear[year] || [];
            const pendingCount = yearPlans.filter((p) => p.status === "pending").length;
            const acceptedCount = yearPlans.filter((p) => p.status === "accepted").length;
            const rejectedCount = yearPlans.filter((p) => p.status === "rejected").length;
            
            return (
              <div
                key={year}
                className="border-b pb-2 flex justify-between items-center hover:bg-gray-50 rounded px-1"
              >
                <button
                  onClick={() => handleViewYearPlans(year)}
                  className="text-blue-500 hover:text-blue-700 underline text-left flex-1 min-w-0"
                >
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-semibold">Year {year}</span>
                      <span className="text-xs text-gray-500">
                        ({yearPlans.length} plans)
                      </span>
                      {pendingCount > 0 && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                          {pendingCount} pending
                        </span>
                      )}
                      {acceptedCount > 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                          {acceptedCount} accepted
                        </span>
                      )}
                      {rejectedCount > 0 && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                          {rejectedCount} rejected
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      ) : !loading ? (
        <div className="flex flex-col items-center justify-center py-4 text-gray-500">
          <p>No plans added</p>
          <p className="text-sm">Employee can create APDP plans from their dashboard</p>
        </div>
      ) : null}

      {/* View Plan Modal */}
      {isViewModalOpen && selectedPlan && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => {
                setIsViewModalOpen(false);
                setSelectedPlan(null);
              }}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
            >
              <X size={24} />
            </button>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                {getStatusIcon(selectedPlan.status)}
                <h2 className="text-xl font-bold text-[#241763]">Plan Details</h2>
                <span className={`ml-auto px-3 py-1 rounded text-xs font-medium capitalize ${getStatusColor(selectedPlan.status)}`}>
                  {selectedPlan.status}
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Performance Year</label>
                  <p className="text-gray-900">{selectedPlan.performanceYear}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                  <p className="text-gray-900">{selectedPlan.planName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activities</label>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedPlan.activities}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Date</label>
                  <p className="text-gray-900">
                    {selectedPlan.targetDate ? new Date(selectedPlan.targetDate).toLocaleDateString() : "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Outcome</label>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedPlan.expectedOutcome}</p>
                </div>
                {selectedPlan.adminNotes && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Notes</label>
                    <p className="text-sm text-gray-700">{selectedPlan.adminNotes}</p>
                  </div>
                )}
                {selectedPlan.createdAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                    <p className="text-gray-900">
                      {new Date(selectedPlan.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
                {selectedPlan.status === "pending" && (
                  <>
                    <button
                      onClick={() => {
                        setIsViewModalOpen(false);
                        handleAcceptClick(selectedPlan);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
                    >
                      <CheckCircle size={16} />
                      Accept
                    </button>
                    <button
                      onClick={() => {
                        setIsViewModalOpen(false);
                        handleRejectClick(selectedPlan);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
                    >
                      <X size={16} />
                      Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    setSelectedPlan(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accept/Reject Modal */}
      {isActionModalOpen && selectedPlan && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col relative">
            {/* Modal Header */}
            <div className={`p-6 flex items-center justify-between ${
              actionType === "accept" 
                ? "bg-gradient-to-r from-green-50 to-green-100 border-b-2 border-green-200" 
                : "bg-gradient-to-r from-red-50 to-red-100 border-b-2 border-red-200"
            }`}>
              <div className="flex items-center gap-3">
                {actionType === "accept" ? (
                  <div className="bg-green-500 rounded-full p-2">
                    <CheckCircle size={24} className="text-white" />
                  </div>
                ) : (
                  <div className="bg-red-500 rounded-full p-2">
                    <AlertCircle size={24} className="text-white" />
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-[#241763]">
                    {actionType === "accept" ? "Accept Plan" : "Reject Plan"}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {actionType === "accept" 
                      ? "Approve this development plan for the performance year" 
                      : "Provide feedback so the employee can improve and resubmit"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsActionModalOpen(false);
                  setSelectedPlan(null);
                  setAdminNotes("");
                  setActionType(null);
                }}
                className="text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
              {/* Plan Details */}
              <div className="bg-white rounded-lg p-5 mb-5 border-2 border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Plan Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plan Name
                    </label>
                    <p className="text-gray-900 font-semibold">{selectedPlan.planName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Performance Year
                    </label>
                    <p className="text-gray-900 font-semibold">{selectedPlan.performanceYear}</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Activities
                    </label>
                    <p className="text-gray-700 whitespace-pre-wrap text-sm">{selectedPlan.activities}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Date
                    </label>
                    <p className="text-gray-700 text-sm">
                      {selectedPlan.targetDate ? new Date(selectedPlan.targetDate).toLocaleDateString() : "N/A"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expected Outcome
                    </label>
                    <p className="text-gray-700 whitespace-pre-wrap text-sm">{selectedPlan.expectedOutcome}</p>
                  </div>
                </div>
              </div>

              {/* Warning for Rejection */}
              {actionType === "reject" && (
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-5">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-800 mb-1">
                        Admin Comments Required
                      </p>
                      <p className="text-xs text-red-700 leading-relaxed">
                        Please provide detailed feedback explaining why this plan is being rejected. The employee will be able to see your comments and edit the plan accordingly before resubmitting.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Notes Section */}
              <div className="bg-white rounded-lg p-5 border-2 border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="text-[#241763]" size={20} />
                  <label className="text-sm font-semibold text-gray-700">
                    Admin Comments
                    {actionType === "reject" && (
                      <span className="text-red-600 font-normal ml-1">* Required</span>
                    )}
                    {actionType === "accept" && (
                      <span className="text-gray-400 font-normal ml-1">(Optional)</span>
                    )}
                  </label>
                </div>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className={`w-full border-2 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#241763] focus:border-transparent ${
                    actionType === "reject" && !adminNotes.trim() 
                      ? "border-red-300 bg-red-50" 
                      : "border-gray-300 bg-white"
                  }`}
                  rows="6"
                  placeholder={
                    actionType === "accept" 
                      ? "Add optional comments, feedback, or suggestions about this plan..." 
                      : "Please provide a detailed reason for rejecting this plan. Be specific about what needs to be improved or changed. The employee will see these comments..."
                  }
                  required={actionType === "reject"}
                />
                {actionType === "reject" && !adminNotes.trim() && (
                  <div className="flex items-center gap-1 mt-2">
                    <AlertCircle size={14} className="text-red-600" />
                    <p className="text-xs text-red-600">Admin comments are required when rejecting a plan</p>
                  </div>
                )}
                {actionType === "accept" && (
                  <p className="text-xs text-gray-500 mt-2">
                    Optional: Add any comments, suggestions, or feedback for the employee
                  </p>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 bg-white p-6">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsActionModalOpen(false);
                    setSelectedPlan(null);
                    setAdminNotes("");
                    setActionType(null);
                  }}
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitAction}
                  disabled={actionType === "reject" && !adminNotes.trim()}
                  className={`px-6 py-2.5 text-white rounded-lg font-semibold flex items-center gap-2 transition-all shadow-md hover:shadow-lg ${
                    actionType === "accept" 
                      ? "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800" 
                      : "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:shadow-none"
                  }`}
                >
                  {actionType === "accept" ? (
                    <>
                      <CheckCircle size={18} />
                      Accept Plan
                    </>
                  ) : (
                    <>
                      <X size={18} />
                      Reject Plan
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Year Plans Modal */}
      {isYearPlansModalOpen && selectedYear && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-5xl w-11/12 max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => {
                setIsYearPlansModalOpen(false);
                setSelectedYear(null);
              }}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
            >
              <X size={24} />
            </button>
            <div className="p-6">
              <h2 className="text-xl font-bold text-[#241763] mb-4">
                Plans for Year {selectedYear}
              </h2>
              <div className="space-y-4">
                {plansByYear[selectedYear]?.map((plan) => {
                  const normalizedPlan = {
                    ...plan,
                    createdDate: plan.createdAt || plan.createdDate,
                  };
                  
                  return (
                    <div
                      key={plan.id}
                      className={`border-2 rounded-lg p-4 ${
                        plan.status === "accepted"
                          ? "border-green-300 bg-green-50"
                          : plan.status === "rejected"
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getStatusIcon(plan.status)}
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                plan.status === "accepted"
                                  ? "bg-green-200 text-green-800"
                                  : plan.status === "rejected"
                                  ? "bg-red-200 text-red-800"
                                  : "bg-yellow-200 text-yellow-800"
                              }`}
                            >
                              {plan.status === "accepted"
                                ? "Accepted"
                                : plan.status === "rejected"
                                ? "Rejected"
                                : "Pending"}
                            </span>
                            {normalizedPlan.createdDate && (
                              <span className="text-xs text-gray-500">
                                {new Date(normalizedPlan.createdDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-gray-800 mb-2">
                            {plan.planName}
                          </h3>
                          <div className="space-y-2 text-sm text-gray-700 mb-3">
                            <div>
                              <span className="font-semibold">Activities:</span>
                              <p className="mt-1 whitespace-pre-wrap">{plan.activities}</p>
                            </div>
                            <div>
                              <span className="font-semibold">Target Date:</span>
                              <p className="mt-1">{plan.targetDate ? new Date(plan.targetDate).toLocaleDateString() : "N/A"}</p>
                            </div>
                            <div>
                              <span className="font-semibold">Expected Outcome:</span>
                              <p className="mt-1 whitespace-pre-wrap">{plan.expectedOutcome}</p>
                            </div>
                          </div>
                          {plan.adminNotes && (
                            <div className="mt-3 p-3 bg-gray-100 rounded border border-gray-200">
                              <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                                <MessageSquare size={14} />
                                Admin Notes:
                              </p>
                              <p className="text-sm text-gray-600 whitespace-pre-wrap">{plan.adminNotes}</p>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {(plan.status !== "accepted" && plan.status !== "completed") && (
                            <>
                              <button
                                onClick={() => {
                                  setIsYearPlansModalOpen(false);
                                  handleAcceptClick(plan);
                                }}
                                className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-colors"
                                title="Accept Plan"
                              >
                                <CheckCircle size={18} />
                              </button>
                              <button
                                onClick={() => {
                                  setIsYearPlansModalOpen(false);
                                  handleRejectClick(plan);
                                }}
                                className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                                title="Reject Plan"
                              >
                                <X size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAPDP;

